import { Component, Injector, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import * as FileSaver from 'file-saver';
import { Subject } from "rxjs";
import { debounceTime, distinctUntilChanged } from "rxjs/operators";
import { UserTab } from "../../../auth/models";
import { FeesHelper } from "../../../common/helpers/fees/fees.helper";
import { Currency, Units } from "../../../common/models";
import { CurrencyRatesService, UnitRatesService } from "../../../common/services";
import { PlattsPricesService } from "../../../indexes/services";
import { BidRateTables, loadBidRateTables } from "../../../common/helpers/unit/bid-unit-rates";
import { ToastService } from "../../../main/services";
import { ROUND_SUPPLIER_STATUSES } from "../../models";
import { ContractOffers } from "../../models/contract-offers/contract-offers";
import { Supplier } from "../../models/supplier/supplier";
import { LocationTagsService } from "../../services/location-tags/location-tags.service";
import {
  TenderBidAnalysisReportService
} from "../../services/tender-bid-analysis-report/tender-bid-analysis-report.service";
import { TenderSuppliersService } from "../../services/tender-suppliers/tender-suppliers.service";

@Component({
    selector: 'jft-bid-fee-report',
    templateUrl: './bid-fee-report.component.html',
    styleUrls: ['./bid-fee-report.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})


export class BidFeeReportComponent implements OnInit {
    public totalPageItems: number;
    public currentPage: number = 1;
    public itemsPerPage: number = 20;
    public hasMore: boolean = true;
    public loadingMore: boolean = false;
    public unitRates;
    // Per-bid tables (decisions #46, #47): each bid at its own rate, else the tender's, else 331.
    public rateTables: BidRateTables | null = null;
    public currencyRates;
    public baseCurrency: Currency;
    public baseUnit: Units;
    public tenderSettings;
    public dateRange: string = '12_month';
    public baseExchangeRateDate: any = null;
    public dateFormat = 'YYYY-MM-DD';
    public currentDateTime: Date = new Date();
    tender: any;
    loading = false;
    public contracts: ContractOffers[];
    public OFFER_STATUSES = ROUND_SUPPLIER_STATUSES
    public selectedLocationTags: number[] = [];
    public locationTagOptions: UserTab[] = [];
    public maxFeeCount: number = 0;
    public feeIndices: number[] = [];
    public feeColumnWidth: string = '5%';
    public searchText: string = '';
    public selectedSuppliers: number[] = [];
    public supplierOptions: Supplier[] = [];
    public companyOptions: { name: string, value: string }[] = [];
    public selectedCompanies: string[] = [];
    public showFuelType: boolean = false;
    public collapsedNotes: { [key: string]: boolean } = {};
    private searchSubject: Subject<string> = new Subject();

    constructor(private route: ActivatedRoute,
                private toast: ToastService,
                private currencyRatesService: CurrencyRatesService,
                private unitRatesService: UnitRatesService,
                private plattsPricesService: PlattsPricesService,
                private service: TenderBidAnalysisReportService,
                private ngbModal: NgbModal,
                private injector: Injector,
                private router: Router,
                private locationTagsService: LocationTagsService,
                private tenderSuppliersService: TenderSuppliersService) {}

    ngOnInit(): void {
        let data = this.route.snapshot.data;
        // Walk up via pathFromRoot because `reports` is a non-empty-path parent,
        // so resolved `tender` data doesn't auto-inherit into this child snapshot.
        this.tender = this.route.snapshot.pathFromRoot
            .map((r) => r.data['tender'])
            .find((t) => t);
        this.contracts = this.route.snapshot.data['contract_offers'] || [];
        this.totalPageItems = this.contracts.length; // Initial load from resolver
        this.hasMore = this.totalPageItems >= this.itemsPerPage;
        this.tenderSettings = data['tenderSettings'];
        this.unitRates = data['unitRates'];

        this.baseCurrency = this.tenderSettings.currency;
        this.baseUnit = this.tenderSettings.units;
        this.initBaseExchangeRateDate();
        this.showFuelType = !this.tender.company.settings.isHiddenField('location_fuel_type', undefined, 'airline');
        this.loadLocationTags();
        this.loadSuppliers();
        this.loadCompanyOptions();

        this.searchSubject.pipe(
            debounceTime(500),
            distinctUntilChanged()
        ).subscribe(searchText => {
            this.searchText = searchText;
            this.onFilterChange();
        });
        // Don't call pageChanged() here - let loadLocationTags() handle it
        // This ensures saved tags are loaded before fetching data

        // Sort contracts by IATA code when data is loaded
        if (this.contracts && Array.isArray(this.contracts)) {
            this.contracts.sort((a, b) => {
                const valueA = a.location?.iata || '';
                const valueB = b.location?.iata || '';
                return valueA.localeCompare(valueB);
            });
        }
    }

    public pageChanged(page = 1, append = false) {
        if (!append) {
            this.contracts = [];
            this.currentPage = 1;
        } else {
            this.loadingMore = true;
        }

        this.service.fetch(this.tender.id, page, this.itemsPerPage, this.selectedLocationTags, this.searchText, this.selectedSuppliers, null, this.selectedCompanies).subscribe(
            data => {
                if (append) {
                    this.contracts = [...this.contracts, ...data];
                } else {
                    this.contracts = data;
                }
                this.initUpdates();
                this.totalPageItems = this.service.getTotalPageItems();
                this.calculateMaxFeeCount();
                this.hasMore = this.contracts.length < this.totalPageItems;
                this.loadingMore = false;
            },
            error => {
                this.loadingMore = false;
                this.toast.error('Log could not be retrieved');
            }
        );
    }

    public onSearch(event: any) {
        this.searchSubject.next(event.target.value);
    }

    public onSupplierFilterChange() {
        this.onFilterChange();
    }

    private onFilterChange() {
        this.currentPage = 1;
        this.pageChanged();
    }

    private loadSuppliers() {
        this.tenderSuppliersService.fetch(this.tender.id, true).subscribe(
            suppliers => {
                this.supplierOptions = suppliers;
            },
            error => console.error('Failed to load suppliers:', error)
        );
    }

    private loadCompanyOptions() {
        this.service.fetchCompanyOptions(this.tender.id).subscribe(
            options => this.companyOptions = options,
            error => { this.companyOptions = []; }
        );
    }

    public onCompanyFilterChange() {
        this.currentPage = 1;
        this.pageChanged();
    }

    public loadMore() {
        if (this.loadingMore || !this.hasMore) return;
        this.currentPage++;
        this.pageChanged(this.currentPage, true);
    }

    private initBaseExchangeRateDate() {
        if (this.tenderSettings.exchangeRateDate && this.tenderSettings.isExchangeRateFixed) {
            this.baseExchangeRateDate = this.tenderSettings.exchangeRateDate.format(this.dateFormat);
        }
    }

    public initUpdates(){
        this.getCurrencyRates();
        this.calculateMaxFeeCount();
    }

    public getCurrencyRates() {
        this.currencyRatesService.fetch(this.baseCurrency.code, {
            tender: this.tender.id,
            date: this.baseExchangeRateDate
        }, false).subscribe((currencies: []) => {
            this.currencyRates = currencies;
            loadBidRateTables(this.unitRatesService, this.unitRates, this.allBids()).subscribe(tables => {
                this.rateTables = tables;
                this.updateOffers();
            });
        })
    }

    /**
     * Calculate the maximum number of fees across all offers in all contracts
     * and generate the fee indices array for template iteration
     */
    public calculateMaxFeeCount(): void {
        let maxCount = 0;

        // Check all contracts
        if (this.contracts && Array.isArray(this.contracts)) {
            this.contracts.forEach(contract => {
                // Check current offers
                if (contract.currentOffers && Array.isArray(contract.currentOffers)) {
                    contract.currentOffers.forEach(offer => {
                        if (offer.bid) {
                            // Check both exchanged_volume_based_fees (after conversion) and volume_based_fees (original)
                            const fees = offer.bid['exchanged_fees_display'] || offer.bid['exchanged_volume_based_fees'] || offer.bid['volume_based_fees'];
                            if (fees && Array.isArray(fees)) {
                                const feeCount = fees.length;
                                if (feeCount > maxCount) {
                                    maxCount = feeCount;
                                }
                            }
                        }
                    });
                }

                // Check all offers (bids)
                if (contract.offers && Array.isArray(contract.offers)) {
                    contract.offers.forEach(offer => {
                        if (offer.bid) {
                            // Check both exchanged_volume_based_fees (after conversion) and volume_based_fees (original)
                            const fees = offer.bid['exchanged_fees_display'] || offer.bid['exchanged_volume_based_fees'] || offer.bid['volume_based_fees'];
                            if (fees && Array.isArray(fees)) {
                                const feeCount = fees.length;
                                if (feeCount > maxCount) {
                                    maxCount = feeCount;
                                }
                            }
                        }
                    });
                }

                // Check mock offers
                if (contract.mockOffers && Array.isArray(contract.mockOffers)) {
                    contract.mockOffers.forEach(offer => {
                        if (offer.bid) {
                            const fees = offer.bid['exchanged_fees_display'] || offer.bid['exchanged_volume_based_fees'] || offer.bid['volume_based_fees'];
                            if (fees && Array.isArray(fees)) {
                                const feeCount = fees.length;
                                if (feeCount > maxCount) {
                                    maxCount = feeCount;
                                }
                            }
                        }
                    });
                }
            });
        }

        this.maxFeeCount = maxCount;
        // Create array of indices for template iteration [0, 1, 2, ...]
        this.feeIndices = Array.from({length: maxCount}, (_, i) => i);

        // Calculate dynamic column width
        // Formula: Available space / (number of fee columns + supplier column + total column)
        // For current contracts: supplier + fees + total
        // For bids: award + supplier + fees + total
        if (maxCount > 0) {
            // Approximate: 15% for supplier, 10% for total, rest divided among fees
            const availableForFees = 75; // percentage
            this.feeColumnWidth = `${Math.floor(availableForFees / maxCount)}%`;
        }

        console.log('Max fee count calculated:', maxCount, 'Fee indices:', this.feeIndices);
    }

    /** The Jet A-1 table a bid's fees convert with; the tender's until the per-bid ones load. */
    public jetTableOf(bid) {
        return this.rateTables ? this.rateTables.ofBid(bid).jet : this.unitRates;
    }

    private allBids(): any[] {
        const bids = [];
        (this.contracts || []).forEach(contract => [contract.offers, contract.currentOffers, contract.mockOffers]
            .forEach(list => (list || []).forEach(offer => offer?.bid && bids.push(offer.bid))));
        return bids;
    }

    public calcTotalFees(bid) {
        return FeesHelper.calcTotalFees(bid, this.currencyRates, this.baseCurrency.code, this.jetTableOf(bid), this.baseUnit.code);
    }

    public updateOffers(){
        this.contracts.forEach(contract => {
            this._updateOffers(contract.offers);
            this._updateOffers(contract.currentOffers);
            if (contract.mockOffers) {
                this._updateOffers(contract.mockOffers);
            }
        })
        // Recalculate max fee count after offers are updated
        this.calculateMaxFeeCount();
    }

    private _updateOffers(offers: any[]) {
        offers.forEach(offer => {
            let bid = offer.bid;

            if (bid.volume_based_fees) {
                bid['volume_fees_total'] = Number(this.calcTotalFees(bid));
                // The airline's own "Custom Volume Fees & Taxes" count toward
                // volume_fees_total, so list them alongside the supplier fees.
                bid['exchanged_fees_display'] = [
                    ...(bid['exchanged_volume_based_fees'] || []),
                    ...(bid['exchanged_volume_based_fees_airline_input'] || []),
                ];
            }
        });

        // Sort offers: awarded first, then by volume_fees_total
        offers.sort((a, b) => {
            const aIsAwarded = a.status === 'AWARDED' || a.status === 'TENTATIVE_AWARDED';
            const bIsAwarded = b.status === 'AWARDED' || b.status === 'TENTATIVE_AWARDED';

            // If one is awarded and the other isn't, awarded comes first
            if (aIsAwarded && !bIsAwarded) return -1;
            if (!aIsAwarded && bIsAwarded) return 1;

            // If both have same awarded status, sort by volume_fees_total
            const aFees = a.bid?.['volume_fees_total'] || 0;
            const bFees = b.bid?.['volume_fees_total'] || 0;
            return aFees - bFees;
        });
    }

    public redirectSelectContract(event){
        this.router.navigate(['/tenders/'+this.tender.id+'/analytics-settings/tender-current-contract']);
    }

    public downloadAsPDF(): void {
        // Use totalPageItems (total location count) instead of contracts.length (current page only)
        const totalLocations = this.totalPageItems || 0;

        // Get location tag filter string
        const locationTagFilter = this.selectedLocationTags.length > 0
            ? this.selectedLocationTags.join(',')
            : undefined;

        console.log('Fee Report - Total locations:', totalLocations);
        console.log('Fee Report - Contracts on current page:', this.contracts?.length);
        console.log('Fee Report - Location tag filter:', locationTagFilter);

        // For large reports (>10 locations), always use async email delivery
        if (totalLocations > 10){
            console.log('Opening modal for large fee report');
            this.openPdfModal();
            return;
        }

        console.log('Attempting direct download for small fee report');
        // For small reports (≤10 locations), direct download
        this.loading = true;
        this.service.getFeePdf(this.tender.id, locationTagFilter, this.searchText, this.selectedSuppliers, this.selectedCompanies).subscribe(
            data => {
                FileSaver.saveAs(data, `bid_fee_report_${this.tender.name}.pdf`);
                this.loading = false;
            },
            error => {
                this.loading = false;
                console.log('Error response:', error);
                // If backend returns 400 (too large), show modal as fallback
                if (error.status === 400 && error.error?.requires_async) {
                    console.log('Backend says fee report too large, opening modal');
                    this.openPdfModal();
                } else {
                    this.toast.error('Error generating fee report');
                }
            }
        );
    }

    public openPdfModal() {
        // Get location tag filter string
        const locationTagFilter = this.selectedLocationTags.length > 0
            ? this.selectedLocationTags.join(',')
            : undefined;

        // Show initial toast that file is being prepared
        this.toast.info('Preparing Fee Analysis Report in background. You will be notified when ready.');
        this.loading = true;

        this.service.getPdfFeeDelayed(this.tender.id, locationTagFilter, this.searchText, this.selectedSuppliers, this.selectedCompanies).subscribe(
            (response: any) => {
                this.loading = false;
                // The notification system will show a toast when file is ready
            },
            error => {
                this.loading = false;
                this.toast.error('Error requesting fee report. Please try again.');
            }
        );
    }

    // Location tags methods
    private loadLocationTags() {
        this.locationTagsService.fetchLocationTags().subscribe(
            (tags: UserTab[]) => {
                this.locationTagOptions = tags;
                // Load previously selected tags from localStorage and validate them
                const savedTags = this.locationTagsService.loadSelectedTags();
                this.selectedLocationTags = this.locationTagsService.validateSelectedTags(savedTags, tags);
                // Always fetch data after tags are loaded (whether tags exist or not)
                this.pageChanged();
            },
            (error) => {
                console.error('Failed to load location tags:', error);
                this.selectedLocationTags = [];
                // Even on error, fetch data without tag filter
                this.pageChanged();
            }
        );
    }

    public onLocationTagFilterChange() {
        // Save selected tags to localStorage for this user
        this.locationTagsService.saveSelectedTags(this.selectedLocationTags);
        this.currentPage = 1;
        this.pageChanged();
    }

    public toggleNoteCollapse(noteId: string): void {
        this.collapsedNotes[noteId] = !this.collapsedNotes[noteId];
    }

    public isNoteCollapsed(noteId: string): boolean {
        return this.collapsedNotes[noteId] || false;
    }

    public shouldTruncateNote(note: string): boolean {
        return note && note.length > 255;
    }

    public getTruncatedNote(note: string): string {
        if (!note) return '';
        return note.length > 255 ? note.substring(0, 255) + '...' : note;
    }

    public getNoteId(offer: any, section: string): string {
        return `${section}_${offer.bid?.id || offer.id}_note`;
    }
}
